import { z } from 'zod';
import { AppSdkError } from './request.mjs';
import { presentSdkFailure, sdkFailureMessage } from './error-presentation.mjs';

// Consumer validation of DATA's C8 result. This is not a receipt lookup,
// authority check or persistence implementation. Canonical types are DATA's.
const id = z.string().min(1).max(200);
const counter = z.number().int().nonnegative().safe();
const rowVersion = z.object({ collectionId: id, rowId: id, version: id.nullable() }).strict();
const versions = z
  .array(rowVersion)
  .max(10000)
  .refine(
    (items) =>
      new Set(items.map((row) => JSON.stringify([row.collectionId, row.rowId]))).size ===
      items.length,
  );
const failure = z
  .object({
    code: z.enum([
      'NOT_AUTHORIZED',
      'INVALID_INPUT',
      'INVALID_COLLECTION_VALUES',
      'OPERATION_DIGEST_MISMATCH',
      'OPERATION_EXPIRED',
      'APP_FENCED',
      'UNAVAILABLE',
      'LIMIT_EXCEEDED',
      'CONNECTION_REQUIRED',
      'FUNCTION_WRITES_NOT_ALLOWED',
    ]),
    retryable: z.boolean(),
  })
  .strict();
const refused = z.object({ ok: z.literal(false), error: failure }).strict();
const mutationResult = z.union([
  z
    .object({
      ok: z.literal(true),
      value: z
        .object({
          changeId: id,
          receiptId: id,
          newVersions: versions,
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ok: z.literal(false),
      conflict: z
        .object({
          code: z.enum([
            'ROW_VERSION_CONFLICT',
            'SCHEMA_VERSION_CONFLICT',
            'DATA_GENERATION_CONFLICT',
          ]),
          currentSchemaVersion: counter.min(1),
          currentDataGeneration: counter,
          currentVersions: versions,
        })
        .strict(),
    })
    .strict(),
  refused,
]);

export function parseMutationResult(value) {
  const parsed = mutationResult.safeParse(value);
  if (!parsed.success) throw new AppSdkError('INVALID_MUTATION_RESULT');
  return presentSdkFailure(parsed.data, sdkFailureMessage(value));
}

export function parseCollectionFailure(value) {
  const parsed = refused.safeParse(value);
  if (!parsed.success) throw new AppSdkError('INVALID_QUERY_RESULT');
  return presentSdkFailure(parsed.data, sdkFailureMessage(value));
}
